import { useEffect, useRef } from "react";

declare global {
  interface Window {
    __INTELLIGO_TURNSTILE_LOAD_PROMISE__?: Promise<void>;
    turnstile?: {
      render: (container: HTMLElement, options: Record<string, unknown>) => string;
      remove?: (widgetId: string) => void;
      reset: (widgetId?: string) => void;
    };
  }
}

const TURNSTILE_SCRIPT_ID = "intelligo-turnstile-script";
const TURNSTILE_SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

const ensureTurnstileScript = () => {
  if (window.turnstile) {
    return Promise.resolve();
  }
  if (window.__INTELLIGO_TURNSTILE_LOAD_PROMISE__) {
    return window.__INTELLIGO_TURNSTILE_LOAD_PROMISE__;
  }
  window.__INTELLIGO_TURNSTILE_LOAD_PROMISE__ = new Promise<void>((resolve, reject) => {
    const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID) as HTMLScriptElement | null;
    if (existingScript) {
      if (window.turnstile) {
        resolve();
        return;
      }
      existingScript.addEventListener("load", () => resolve(), { once: true });
      existingScript.addEventListener("error", () => reject(new Error("Turnstile script failed to load.")), { once: true });
      return;
    }
    const script = document.createElement("script");
    script.id = TURNSTILE_SCRIPT_ID;
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener("load", () => resolve(), { once: true });
    script.addEventListener("error", () => reject(new Error("Turnstile script failed to load.")), { once: true });
    document.head.appendChild(script);
  });
  return window.__INTELLIGO_TURNSTILE_LOAD_PROMISE__;
};

type TurnstileWidgetProps = {
  onTokenChange: (value: string) => void;
  resetKey: number;
  siteKey: string;
  token: string;
};

export const TurnstileWidget = ({
  onTokenChange,
  resetKey,
  siteKey,
  token,
}: TurnstileWidgetProps) => {
  const turnstileContainerRef = useRef<HTMLDivElement | null>(null);
  const turnstileWidgetIdRef = useRef<null | string>(null);

  useEffect(() => {
    if (!siteKey || !turnstileContainerRef.current) {
      return;
    }
    let isCancelled = false;
    void ensureTurnstileScript()
      .then(() => {
        if (isCancelled || !turnstileContainerRef.current || turnstileWidgetIdRef.current || !window.turnstile) {
          return;
        }
        turnstileWidgetIdRef.current = window.turnstile.render(turnstileContainerRef.current, {
          sitekey: siteKey,
          theme: "auto",
          callback: (resolvedToken: string) => onTokenChange(resolvedToken),
          "expired-callback": () => onTokenChange(""),
          "error-callback": () => onTokenChange(""),
        });
      })
      .catch(() => {
        onTokenChange("");
      });
    return () => {
      isCancelled = true;
      if (turnstileWidgetIdRef.current && window.turnstile?.remove) {
        window.turnstile.remove(turnstileWidgetIdRef.current);
      }
      turnstileWidgetIdRef.current = null;
    };
  }, [onTokenChange, siteKey]);

  useEffect(() => {
    if (!turnstileWidgetIdRef.current || !window.turnstile) {
      return;
    }
    onTokenChange("");
    window.turnstile.reset(turnstileWidgetIdRef.current);
  }, [onTokenChange, resetKey]);

  if (!siteKey) {
    return null;
  }

  return (
    <div
      aria-live="polite"
      className={`auth-turnstile${token ? " is-complete" : ""}`}
      ref={turnstileContainerRef}
    />
  );
};
